import React from 'react';
import { Box, IconButton, TextField, MenuItem } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useWatch, Control, Controller } from 'react-hook-form';
import { debounce } from 'lodash';
import { InvoiceScheme, InvoiceType } from '../../Types/invoicesTypes';
import type { VatRateType } from '../../Types/invoicesTypes';
import type { CreateInvoiceForm, CreateInvoiceItemForm } from '../../Types/createInvoiceFormTypes';
import {
  getAllowedVatTypesForInvoice,
  isVatExempt,
  getDefaultVatExemptNote,
  calculateGrossFromNet,
  getDisplayLabel,
  getVatRate,
  normalizeVatType,
} from '../../utils/vatMappings';
import { normalizeInvoiceScheme, normalizeInvoiceType } from '../../utils/invoiceEnumNormalization';
import {
  useGetAllowedVatRatesForSchemeWithLabelsQuery,
  useCalculateInvoiceItemMutation,
} from '../../Service/extendedInvoicesApi';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { invoiceLogger } from '../../utils/invoiceLogger';

interface InvoiceItemRowProps {
  index: number;
  control: Control<CreateInvoiceForm>;
  onItemChange: (index: number, field: keyof CreateInvoiceItemForm, value: any) => void;
  onDelete: (index: number) => void;
  prevVatTypesRef: React.MutableRefObject<Map<number, VatRateType>>;
}

/**
 * Invoice Item Row Component (KROK 10)
 *
 * Implements:
 * - 10.1: Dynamic VAT type dropdown based on scheme and invoice type
 * - 10.2: Auto-fill vatExemptNote when VAT type changes
 * - 10.3: Computed unitGross (readonly) based on unitNet and vatType
 *
 * ✅ MEMOIZED to prevent unnecessary re-renders that cause focus loss
 */
const InvoiceItemRow: React.FC<InvoiceItemRowProps> = ({
  index,
  control,
  onItemChange,
  onDelete,
  prevVatTypesRef,
}) => {
  // Watch form-level values for dynamic dropdown filtering (KROK 10.1)
  const rawScheme = useWatch({ control, name: 'scheme' }) as unknown;
  const rawInvoiceType = useWatch({ control, name: 'type' }) as unknown;
  const hasSchemeContext =
    rawScheme !== undefined && rawScheme !== null && String(rawScheme).trim().length > 0;
  const hasInvoiceTypeContext =
    rawInvoiceType !== undefined &&
    rawInvoiceType !== null &&
    String(rawInvoiceType).trim().length > 0;
  const normalizedScheme = React.useMemo(() => normalizeInvoiceScheme(rawScheme), [rawScheme]);
  const normalizedInvoiceType = React.useMemo(
    () => normalizeInvoiceType(rawInvoiceType),
    [rawInvoiceType]
  );

  // Watch item-level values for computed fields and auto-fill (KROK 10.2, 10.3)
  // ✅ CRITICAL: Only watch fields that affect calculations/logic, NOT text inputs
  const vatType = useWatch({ control, name: `items.${index}.vatType` }) as VatRateType;
  const unitNet = useWatch({ control, name: `items.${index}.unitNet` }) as number;
  const quantity = useWatch({ control, name: `items.${index}.quantity` }) as number;
  const vatExemptNote = useWatch({ control, name: `items.${index}.vatExemptNote` }) as string;
  const currentUnitGross = useWatch({ control, name: `items.${index}.unitGross` }) as number;

  // ✅ API Hook 1: Get allowed VAT types (cached, 5 min)
  const {
    data: allowedVatTypesFromApi,
    isLoading: isLoadingVatTypes,
    error: vatTypesError,
  } = useGetAllowedVatRatesForSchemeWithLabelsQuery(
    { scheme: normalizedScheme, type: normalizedInvoiceType },
    {
      skip: !hasSchemeContext || !hasInvoiceTypeContext,
      refetchOnMountOrArgChange: 300, // 5 minutes
    }
  );

  // ✅ API Hook 2: Calculate item (NOT cached, always fresh)
  const [calculateItem, { isLoading: isCalculating, error: calculationError }] =
    useCalculateInvoiceItemMutation();

  // ✅ Error Handler: Handle API errors with fallback
  const [hasApiError, setHasApiError] = React.useState(false);

  const { handleError } = useApiErrorHandler({
    onRateLimitError: () => {
      invoiceLogger.warn('Rate limit hit. Using local calculation as fallback.');
      setHasApiError(true);
    },
    onServerError: () => {
      invoiceLogger.error('Server error. Using local calculation as fallback.');
      setHasApiError(true);
    },
    onNetworkError: () => {
      invoiceLogger.error('Network error. Using local calculation as fallback.');
      setHasApiError(true);
    },
    fallbackAction: () => {
      // Fallback is handled in debouncedCalculate catch block
    },
    showToast: (message, severity) => {
      // Optional: User can wrap component with NotificationProvider to enable toasts
      invoiceLogger.debug(`[${severity.toUpperCase()}] ${message}`);
    },
  });

  // Log errors when they occur
  React.useEffect(() => {
    if (vatTypesError) {
      handleError(vatTypesError);
    }
  }, [vatTypesError, handleError]);

  React.useEffect(() => {
    if (calculationError) {
      handleError(calculationError);
    }
  }, [calculationError, handleError]);

  // Clear error state when successful
  React.useEffect(() => {
    if (!vatTypesError && !calculationError && hasApiError) {
      setHasApiError(false);
    }
  }, [vatTypesError, calculationError, hasApiError]);

  // KROK 10.1: Use allowed VAT types from API (with fallback to local function)
  const allowedVatTypes = React.useMemo(() => {
    // Use API data if available
    if (allowedVatTypesFromApi && allowedVatTypesFromApi.length > 0) {
      // Normalize API values: vat23 → Vat23, vatZW → VatZW
      const normalized = allowedVatTypesFromApi.map((opt) => normalizeVatType(opt.value));
      invoiceLogger.debug(`[InvoiceItemRow] Allowed VAT types from API (item ${index}):`, {
        scheme: rawScheme,
        invoiceType: rawInvoiceType,
        raw: allowedVatTypesFromApi.map((opt) => opt.value),
        normalized,
        currentVatType: vatType,
      });
      invoiceLogger.debug('[InvoiceItemRow] VAT_SELECT_CONTEXT', {
        index,
        source: 'api',
        scheme: rawScheme,
        invoiceType: rawInvoiceType,
        currentVatType: vatType,
        allowedVatTypes: normalized,
      });
      return normalized;
    }

    // Fallback to local function if API not loaded yet
    if (!hasSchemeContext || !hasInvoiceTypeContext) {
      const fallback = getAllowedVatTypesForInvoice(InvoiceScheme.Standard, InvoiceType.Standard);
      invoiceLogger.debug(
        `[InvoiceItemRow] Allowed VAT types fallback (no scheme/type, item ${index}):`,
        fallback
      );
      return fallback;
    }

    // getAllowedVatTypesForInvoice already returns VatRateType[] - use it directly
    const localTypes = getAllowedVatTypesForInvoice(normalizedScheme, normalizedInvoiceType);
    invoiceLogger.debug(`[InvoiceItemRow] Allowed VAT types from local function (item ${index}):`, {
      scheme: normalizedScheme,
      invoiceType: normalizedInvoiceType,
      types: localTypes,
      currentVatType: vatType,
    });
    invoiceLogger.debug('[InvoiceItemRow] VAT_SELECT_CONTEXT', {
      index,
      source: 'local',
      scheme: normalizedScheme,
      invoiceType: normalizedInvoiceType,
      currentVatType: vatType,
      allowedVatTypes: localTypes,
    });
    return localTypes;
  }, [
    allowedVatTypesFromApi,
    rawScheme,
    rawInvoiceType,
    hasSchemeContext,
    hasInvoiceTypeContext,
    normalizedScheme,
    normalizedInvoiceType,
    index,
    vatType,
  ]);

  // ✅ Store stable reference to onItemChange (prevents useEffect infinite loop)
  const onItemChangeRef = React.useRef(onItemChange);
  React.useEffect(() => {
    onItemChangeRef.current = onItemChange;
  }, [onItemChange]);

  // ✅ Track last auto-filled note to prevent re-triggering useEffect
  const lastAutoFilledNoteRef = React.useRef<string | null>(null);

  // ✅ KROK 10.3: Debounced calculation with backend API
  const debouncedCalculate = React.useMemo(
    () =>
      debounce(async (unitNet: number, quantity: number, vatType: VatRateType) => {
        const hasUnitNet = unitNet !== undefined && unitNet !== null;
        const hasQuantity = quantity !== undefined && quantity !== null;

        if (!hasUnitNet || !hasQuantity || !vatType) {
          return;
        }

        // Normalize VAT type to handle case differences
        const normalizedVatType = normalizeVatType(vatType);

        // Backend expects camelCase (vat23), but we store PascalCase (Vat23)
        // Convert back to camelCase for API call
        const vatTypeForApi =
          normalizedVatType.charAt(0).toLowerCase() + normalizedVatType.slice(1);

        try {
          const result = await calculateItem({
            unitNet,
            quantity,
            vatType: vatTypeForApi as VatRateType,
            vatRate: getVatRate(normalizedVatType),
          }).unwrap();

          // Update computed fields from backend (only unitGross is in form)
          onItemChangeRef.current(index, 'unitGross', result.unitGross);
          // Note: totalGross, totalVat, unitVat are computed on backend submission
        } catch (error: any) {
          // Enhanced error handling with specific fallback messages
          invoiceLogger.error('Backend calculation error:', error);

          // Check error type for user feedback
          if (error?.status === 429) {
            invoiceLogger.warn('Rate limit exceeded. Using local calculation.');
          } else if (error?.status === 500) {
            invoiceLogger.error('Server error. Using local calculation.');
          } else if (error?.status === 'FETCH_ERROR') {
            invoiceLogger.error('Network error. Using local calculation.');
          }

          // Always fallback to frontend calculation if API fails
          const fallbackGross = calculateGrossFromNet(unitNet, normalizedVatType);
          onItemChangeRef.current(index, 'unitGross', fallbackGross);
        }
      }, 500), // 500ms debounce
    [calculateItem, index]
  );

  // Trigger calculation when values change
  React.useEffect(() => {
    invoiceLogger.debug(
      `[InvoiceItemRow] 🧮 useEffect [calculation] triggered for item ${index}:`,
      {
        unitNet,
        quantity,
        vatType,
        willCalculate: unitNet !== undefined && unitNet !== null && quantity !== undefined && quantity !== null && !!vatType,
        timestamp: new Date().toISOString(),
      }
    );

    if (unitNet !== undefined && unitNet !== null && quantity !== undefined && quantity !== null && vatType) {
      debouncedCalculate(unitNet, quantity, vatType);
    }

    // Cleanup on unmount
    return () => {
      debouncedCalculate.cancel();
    };
  }, [unitNet, quantity, vatType, debouncedCalculate, index]);

  // ✅ CRITICAL: Use refs to track debounce timers and prevent focus loss
  const debounceTimersRef = React.useRef<Record<string, NodeJS.Timeout>>({});

  // ✅ CRITICAL: Memoized callbacks that DON'T depend on form state
  // This prevents re-creation on every render (which causes focus loss)
  const handleNameChange = React.useCallback(
    (field: any, value: string) => {
      // Update immediately via field.onChange (no re-render)
      field.onChange(value);

      // Clear existing timer
      if (debounceTimersRef.current.name) {
        clearTimeout(debounceTimersRef.current.name);
      }
      // Debounce: notify parent after 300ms
      debounceTimersRef.current.name = setTimeout(() => {
        onItemChangeRef.current(index, 'name', value);
      }, 300);
    },
    [index]
  );

  const handleUnitNetChange = React.useCallback(
    (field: any, value: string) => {
      const numValue = value === '' ? 0 : Number(value);
      field.onChange(numValue);

      if (debounceTimersRef.current.unitNet) {
        clearTimeout(debounceTimersRef.current.unitNet);
      }
      debounceTimersRef.current.unitNet = setTimeout(() => {
        onItemChangeRef.current(index, 'unitNet', numValue);
      }, 300);
    },
    [index]
  );

  const handleVatTypeChange = React.useCallback(
    (value: string) => {
      invoiceLogger.debug(`[InvoiceItemRow] 🔄 handleVatTypeChange called for item ${index}:`, {
        newVatType: value,
        previousVatType: vatType,
        timestamp: new Date().toISOString(),
      });
      onItemChangeRef.current(index, 'vatType', value);
    },
    [index, vatType]
  );

  const handleVatExemptNoteChange = React.useCallback(
    (field: any, value: string) => {
      field.onChange(value);

      if (debounceTimersRef.current.vatExemptNote) {
        clearTimeout(debounceTimersRef.current.vatExemptNote);
      }
      debounceTimersRef.current.vatExemptNote = setTimeout(() => {
        onItemChangeRef.current(index, 'vatExemptNote', value);
      }, 300);
    },
    [index]
  );

  // Cleanup timers on unmount
  React.useEffect(() => {
    const timers = debounceTimersRef.current;
    return () => {
      Object.values(timers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  // KROK 10.2: Auto-fill vatExemptNote when vatType changes OR when component mounts with empty note
  React.useEffect(() => {
    invoiceLogger.debug(`[InvoiceItemRow] 🎯 useEffect [vatType] triggered for item ${index}:`, {
      vatType,
      invoiceType: rawInvoiceType,
      vatExemptNote,
      timestamp: new Date().toISOString(),
    });

    if (!vatType) {
      invoiceLogger.debug(`[InvoiceItemRow] ⏭️ Skipping - no vatType set for item ${index}`);
      return;
    }

    const needsExemptNote = isVatExempt(vatType);
    invoiceLogger.debug(`[InvoiceItemRow] 🔍 Exempt check for item ${index}:`, {
      vatType,
      needsExemptNote,
    });

    if (needsExemptNote) {
      const defaultNote = getDefaultVatExemptNote(vatType, normalizedInvoiceType);
      const isNoteEmpty = !vatExemptNote || vatExemptNote.trim() === '';
      const noteMatchesDefault = vatExemptNote?.trim() === defaultNote?.trim();

      // ✅ Check if this note was auto-filled by us (prevent infinite loop)
      const wasAutoFilled = lastAutoFilledNoteRef.current === defaultNote;

      invoiceLogger.debug(`[InvoiceItemRow] 📝 Auto-fill conditions check for item ${index}:`, {
        defaultNote,
        isNoteEmpty,
        noteMatchesDefault,
        wasAutoFilled,
        currentNote: vatExemptNote,
        lastAutoFilledNote: lastAutoFilledNoteRef.current,
        willAutoFill: defaultNote && (isNoteEmpty || !noteMatchesDefault) && !wasAutoFilled,
      });

      // Auto-fill if:
      // 1. Note is empty OR
      // 2. Note doesn't match current vatType's default (user switched between exempt VAT types)
      // 3. AND this exact note wasn't just auto-filled (prevent re-trigger)
      if (defaultNote && (isNoteEmpty || !noteMatchesDefault) && !wasAutoFilled) {
        invoiceLogger.debug(`[InvoiceItemRow] ✅ Auto-filling vatExemptNote for item ${index}:`, {
          vatType,
          defaultNote,
          currentNote: vatExemptNote,
          reason: isNoteEmpty ? 'note is empty' : 'note does not match default for current vatType',
        });

        // Store what we're about to auto-fill
        lastAutoFilledNoteRef.current = defaultNote;

        // Update immediately (no setTimeout - causes more re-renders)
        onItemChangeRef.current(index, 'vatExemptNote', defaultNote);
      } else {
        invoiceLogger.debug(
          `[InvoiceItemRow] ⏭️ Skipping auto-fill for item ${index} (conditions not met)`
        );
      }
    } else {
      // Clear note if VAT type doesn't require exemption note
      // Only clear if note is not already empty (prevents infinite loops)
      if (vatExemptNote?.trim()) {
        invoiceLogger.debug(
          `[InvoiceItemRow] 🗑️ Clearing vatExemptNote for item ${index} (VAT type doesn't require note)`
        );
        lastAutoFilledNoteRef.current = ''; // Track empty string
        onItemChangeRef.current(index, 'vatExemptNote', '');
      } else {
        invoiceLogger.debug(
          `[InvoiceItemRow] ⏭️ Note already empty for item ${index} (no clear needed)`
        );
      }
    }

    // Update ref for next comparison
    prevVatTypesRef.current.set(index, vatType);
    invoiceLogger.debug(`[InvoiceItemRow] 🏁 useEffect [vatType] completed for item ${index}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatType, normalizedInvoiceType, index]);
  // ✅ REMOVED vatExemptNote from deps - only trigger on vatType change, not note change
  // This prevents infinite loop when we auto-fill the note

  // Check if current item requires vatExemptNote field
  const showExemptNote = React.useMemo(() => {
    const shouldShow = vatType && isVatExempt(vatType);
    invoiceLogger.debug(`[InvoiceItemRow] 👁️ useMemo [showExemptNote] for item ${index}:`, {
      vatType,
      isExempt: vatType ? isVatExempt(vatType) : false,
      shouldShow,
      timestamp: new Date().toISOString(),
    });
    return shouldShow;
  }, [vatType, index]);

  const lineGrossTotal = React.useMemo(() => {
    const normalizedQuantity = Number(quantity ?? 0);
    const normalizedUnitGross = Number(currentUnitGross ?? 0);

    return normalizedQuantity * normalizedUnitGross;
  }, [currentUnitGross, quantity]);

  return (
    <Box
      mb={1}
      sx={{
        background: '#f7f7f7',
        p: 2,
        borderRadius: 1,
        display: 'grid',
        gap: 2,
        alignItems: 'start',
        gridTemplateColumns: {
          xs: '1fr',
          lg: 'minmax(220px, 2fr) 110px 140px 210px auto',
        },
      }}
    >
      {/* Row 1: Basic item info */}
      <Controller
        name={`items.${index}.name`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            onChange={(e) => handleNameChange(field, e.target.value)}
            label="Nazwa pozycji"
            size="small"
            autoComplete="off"
            sx={{ minWidth: 0 }}
          />
        )}
      />
      <Controller
        name={`items.${index}.quantity`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Ilość"
            size="small"
            type="number"
            onChange={(e) => {
              const val = e.target.value;
              const numValue = val === '' ? 1 : Number(val);
              field.onChange(numValue);
              onItemChange(index, 'quantity', numValue);
            }}
            sx={{ minWidth: 0 }}
          />
        )}
      />

      {/* Row 2: Pricing and VAT */}
      <Controller
        name={`items.${index}.unitNet`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            onChange={(e) => handleUnitNetChange(field, e.target.value)}
            label="Cena netto"
            size="small"
            type="number"
            autoComplete="off"
            sx={{ minWidth: 0 }}
          />
        )}
      />

      {/* KROK 10.1: Dynamic VAT Type dropdown with loading state */}
      <Controller
        name={`items.${index}.vatType`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            label="Typ VAT"
            size="small"
            value={field.value ?? ''}
            onChange={(e) => {
              const newValue = e.target.value;
              field.onChange(newValue); // ✅ CRITICAL: Update RHF state first
              handleVatTypeChange(newValue); // Then notify parent
            }}
            disabled={isLoadingVatTypes}
            sx={{ minWidth: 0 }}
            SelectProps={{
              displayEmpty: true,
            }}
            helperText={isLoadingVatTypes ? 'Ładowanie opcji VAT...' : undefined}
          >
            <MenuItem value="">
              <em>{isLoadingVatTypes ? 'Ładowanie...' : 'Wybierz typ VAT'}</em>
            </MenuItem>
            {allowedVatTypes.map((vt) => (
              <MenuItem key={vt} value={vt}>
                {getDisplayLabel(vt)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {/* Delete button */}
      <IconButton
        onClick={() => onDelete(index)}
        color="error"
        sx={{
          alignSelf: { xs: 'flex-start', lg: 'center' },
          justifySelf: { xs: 'flex-start', lg: 'center' },
        }}
      >
        <DeleteIcon />
      </IconButton>

      <Box
        sx={{
          gridColumn: '1 / -1',
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            minWidth: 180,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: hasApiError ? '#ffebee' : 'grey.200',
            border: '1px solid',
            borderColor: hasApiError ? 'error.light' : 'grey.300',
          }}
        >
          <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Cena brutto</Box>
          <Box sx={{ fontSize: 28, lineHeight: 1.1, fontWeight: 500 }}>
            {currentUnitGross ? currentUnitGross.toFixed(2) : '0.00'}
          </Box>
          <Box sx={{ fontSize: 12, color: hasApiError ? 'error.main' : 'text.secondary', mt: 0.5 }}>
            {isCalculating
              ? 'Obliczanie...'
              : hasApiError
                ? 'Obliczenia lokalne po błędzie API'
                : 'Automatycznie wyliczone'}
          </Box>
        </Box>

        <Box
          data-testid={`invoice-item-line-gross-total-${index}`}
          sx={{
            minWidth: 200,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: 'grey.200',
            border: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Suma brutto pozycji</Box>
          <Box sx={{ fontSize: 28, lineHeight: 1.1, fontWeight: 600 }}>
            {lineGrossTotal.toFixed(2)}
          </Box>
          <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Ilość × cena brutto</Box>
        </Box>
      </Box>

      {/* KROK 10.2 & 10.4: Conditional vatExemptNote field (full width) */}
      {showExemptNote && (
        <Controller
          name={`items.${index}.vatExemptNote`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => handleVatExemptNoteChange(field, e.target.value)}
              label="Podstawa prawna zwolnienia z VAT"
              size="small"
              multiline
              rows={2}
              sx={{ gridColumn: '1 / -1', width: '100%' }}
              helperText="Wypełnione automatycznie. Uwaga: Na fakturze pojawi się globalna adnotacja jeśli WSZYSTKIE pozycje mają ten sam typ VAT."
            />
          )}
        />
      )}
    </Box>
  );
};

// Display name for React DevTools
InvoiceItemRow.displayName = 'InvoiceItemRow';

// ✅ Export without React.memo - useWatch triggers re-renders automatically
// React Hook Form handles optimization internally
export default InvoiceItemRow;
